import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Select,
  InputNumber,
  Checkbox,
  message,
  Tag,
  Tooltip,
  Popconfirm,
  Descriptions,
  Divider,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const { Option } = Select;

const TemplateItemsDrawer = ({ visible, onClose, template, onUpdate }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [templateItems, setTemplateItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible && template) {
      fetchTemplateItems();
      fetchAllItems();
    }
  }, [visible, template]);

  const fetchTemplateItems = async () => {
    setLoading(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        `${domainSettings.urls.bot.requirementItems}?requirement_template=${template.id}`,
        null,
        navigate,
      );

      if (response.ok) {
        setTemplateItems(response.data.results || response.data || []);
      } else {
        Utilities.showError(response, "Failed to fetch template items");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllItems = async () => {
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bot.items,
        null,
        navigate,
      );

      if (response.ok) {
        setAllItems(response.data.results || response.data || []);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  };

  const handleDelete = async (itemId) => {
    try {
      const response = await Utilities.fetchWithAuth(
        "DELETE",
        domainSettings.urls.bot.requirementItemDetail.replace(":id", itemId),
        null,
        navigate,
      );

      if (response.ok) {
        message.success("Item removed from template");
        fetchTemplateItems();
        onUpdate();
      } else {
        Utilities.showError(response, "Failed to remove item");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        requirement_template: template.id,
        bot_item: values.bot_item,
        quantity: values.quantity,
        is_mandatory: values.is_mandatory,
      };

      const method = editingItem ? "PUT" : "POST";
      const url = editingItem
        ? domainSettings.urls.bot.requirementItemDetail.replace(
            ":id",
            editingItem.id,
          )
        : domainSettings.urls.bot.requirementItems;

      const response = await Utilities.fetchWithAuth(
        method,
        url,
        payload,
        navigate,
      );

      if (response.ok) {
        message.success(
          editingItem
            ? "Item updated successfully"
            : "Item added to template successfully",
        );
        setModalVisible(false);
        setEditingItem(null);
        form.resetFields();
        fetchTemplateItems();
        onUpdate();
      } else {
        Utilities.showError(response, "Failed to save item");
      }
    } catch (error) {
      message.error("An error occurred");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    form.setFieldsValue({
      bot_item: item.bot_item,
      quantity: item.quantity,
      is_mandatory: item.is_mandatory,
    });
    setModalVisible(true);
  };

  // Get available items (not already in template)
  const availableItems = allItems.filter((item) => {
    return !templateItems.some((ti) => ti.bot_item === item.id);
  });

  const columns = [
    {
      title: "Item Name",
      dataIndex: "item_name",
      key: "item_name",
    },
    {
      title: "Category",
      dataIndex: "category_name",
      key: "category_name",
      render: (text) => text || "N/A",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (qty) => <Tag color="blue">{qty}</Tag>,
    },
    {
      title: "Mandatory",
      dataIndex: "is_mandatory",
      key: "is_mandatory",
      render: (mandatory) => (
        <Tag color={mandatory ? "red" : "orange"}>
          {mandatory ? "Required" : "Optional"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Remove this item from template?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Remove">
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!template) return null;

  return (
    <Drawer
      title={`Manage Items: ${template.name}`}
      placement="right"
      onClose={onClose}
      open={visible}
      width={window.innerWidth > 768 ? 900 : "100%"}
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingItem(null);
            form.resetFields();
            form.setFieldsValue({
              quantity: 1,
              is_mandatory: true,
            });
            setModalVisible(true);
          }}
        >
          Add Item
        </Button>
      }
    >
      <Descriptions
        bordered
        column={{ xs: 1, sm: 2 }}
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label="Template">{template.name}</Descriptions.Item>
        <Descriptions.Item label="Class">
          {template.class_name || "N/A"}
        </Descriptions.Item>
        <Descriptions.Item label="Term">
          {template.term_name || `Term ${template.term}`}
        </Descriptions.Item>
        <Descriptions.Item label="Academic Year">
          {template.acadyr}
        </Descriptions.Item>
        <Descriptions.Item label="Total Items" span={2}>
          <Tag color="blue">{templateItems.length} items</Tag>
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      <Table
        columns={columns}
        dataSource={templateItems}
        rowKey="id"
        loading={loading}
        scroll={{ x: 600 }}
        pagination={{
          pageSize: 10,
          showTotal: (total) => `Total ${total} items`,
        }}
      />

      {/* Add/Edit Item Modal */}
      <Modal
        title={editingItem ? "Edit Item" : "Add Item to Template"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingItem(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            quantity: 1,
            is_mandatory: true,
          }}
        >
          <Form.Item
            label="Item"
            name="bot_item"
            rules={[{ required: true, message: "Please select an item" }]}
          >
            <Select
              placeholder="Select item"
              showSearch
              optionFilterProp="children"
              disabled={!!editingItem}
            >
              {(editingItem ? allItems : availableItems).map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.name} ({item.code}) - {item.category_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Quantity"
            name="quantity"
            rules={[
              { required: true, message: "Please enter quantity" },
              {
                type: "number",
                min: 1,
                message: "Quantity must be at least 1",
              },
            ]}
          >
            <InputNumber
              min={1}
              style={{ width: "100%" }}
              placeholder="How many units required?"
            />
          </Form.Item>

          <Form.Item name="is_mandatory" valuePropName="checked">
            <Checkbox>This item is mandatory (required)</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </Drawer>
  );
};

export default TemplateItemsDrawer;
